import type { State as CompanyState } from '@/pages/CompanyReport/redux/interface';
import type { LocationState } from 'redux-first-router';
import { shallowEqual, useSelector } from 'react-redux';
import { pathConstructor } from '../utils';
import type { CompanyReportSection } from '@/pages/CompanyReport/useSection';
import { getDescription, getSectionSchema, getTitle } from './utils';
import { SWS_HOST } from '@/constants';
import { ROUTE_COMPANY_WITH_EVENTS } from '@/constants/routes';
import { formatterPercentage } from '@/utilities/format';
import { getDividendStatements } from '@/pages/CompanyReport/redux/selectors';

type Selected =
  | { status: 'loading' }
  | {
      status: 'success';
      name: string;
      shortName: string;
      tickerSymbol: string;
      uniqueSymbol: string;
      exchangeSymbol: string;
      exchangeSymbolFiltered: string;
      canonicalUrl: string;
      allowCrawlers: boolean;
      mainHeader: string;
      fundamentals: string;
      dividendGrowing: boolean;
      dividendCovered: boolean;
      latestDividendYield: number | null;
      latestDividendYieldFormatted: string;
      currentDividendPayoutRatioFormatted: string;
      isBank: boolean;
    };
function selector(state: {
  location: LocationState;
  company: CompanyState;
}): Selected {
  const path = pathConstructor(state.location);
  const companyId = state.company.urlToIdMapping[path];
  if (companyId === '' || typeof companyId === 'undefined')
    return { status: 'loading' };
  const dividendStatements = getDividendStatements(state, { companyId });
  const summary = state.company.summary[companyId];
  const analysis = state.company.analysis[companyId];
  const formatting = state.company.formatting[companyId];

  if (!summary || !analysis || !formatting) return { status: 'loading' };

  const allowCrawlers =
    state.location.type === ROUTE_COMPANY_WITH_EVENTS
      ? false
      : summary.isSearchable && summary.isFund === false;
  const dividendGrowing =
    dividendStatements?.isDividendGrowing.value === 'Pass';
  const dividendCovered =
    dividendStatements?.isDividendCovered.value === 'Pass';
  const isBank = dividendStatements?.isBank.value === 'Pass';

  return {
    status: 'success',
    name: summary.name,
    shortName: summary.shortName,
    tickerSymbol: summary.tickerSymbol,
    uniqueSymbol: summary.uniqueSymbol,
    exchangeSymbol: summary.exchangeSymbol,
    exchangeSymbolFiltered: summary.exchangeSymbolFiltered,
    canonicalUrl: summary.canonicalUrl,
    allowCrawlers,
    mainHeader: summary.mainHeader,
    fundamentals: summary.fundamentals,
    dividendGrowing,
    dividendCovered,
    latestDividendYield: analysis.dividendYield,
    latestDividendYieldFormatted: formatterPercentage(analysis.dividendYield),
    currentDividendPayoutRatioFormatted: formatterPercentage(
      analysis.payoutRatio
    ),
    isBank,
  };
}

export function useSEO(section: CompanyReportSection) {
  const selected = useSelector<
    Parameters<typeof selector>[0],
    ReturnType<typeof selector>
  >(selector, shallowEqual);
  const canonicalUrl =
    selected.status === 'success' ? selected.canonicalUrl : '';
  const canonicalUrlWithHost = `${SWS_HOST}${canonicalUrl}`;
  const sectionSuffix = section === 'summary' ? '' : '/' + section;
  const canonicalUrlWithHostAndSection = `${canonicalUrlWithHost}${sectionSuffix}`;

  if (selected.status === 'loading') return { status: selected.status };
  const {
    name,
    shortName,
    tickerSymbol,
    uniqueSymbol,
    exchangeSymbol,
    exchangeSymbolFiltered,
    allowCrawlers,
    mainHeader,
    dividendGrowing,
    dividendCovered,
    latestDividendYield,
    latestDividendYieldFormatted,
    currentDividendPayoutRatioFormatted,
    isBank,
  } = selected;

  const exchangeTicker = `${exchangeSymbolFiltered}:${tickerSymbol}`;
  const tags = [
    shortName,
    uniqueSymbol,
    tickerSymbol,
    exchangeSymbol,
    'stock analysis',
    'news',
  ].join(', ');
  const keywords = `${tags}, ${[
    'PE ratio',
    'intrinsic value',
    'DCF',
    'growth',
    'yield',
    'dividends',
  ].join(', ')}`;

  const title = getTitle({
    companyName: name,
    companyShortName: shortName,
    tickerSymbol,
    exchangeTicker,
    uniqueSymbol,
    section,
  });

  const description = getDescription({
    companyName: name,
    companyShortName: shortName,
    tickerSymbol,
    exchangeTicker,
    section,
    latestDividendYield,
    latestDividendYieldFormatted,
    dividendGrowing,
    dividendCovered,
    currentDividendPayoutRatioFormatted,
    isBank,
  });

  const sectionSchema = getSectionSchema({
    section,
    companyName: shortName,
    url: canonicalUrlWithHost,
    title,
    description,
  });
  return {
    status: selected.status,
    canonicalUrlWithHost: canonicalUrlWithHost,
    canonicalUrlWithHostAndSection: canonicalUrlWithHostAndSection,
    allowCrawlers,
    exchangeTicker,
    companyName: shortName,
    title,
    description,
    canonicalUrl,
    keywords,
    tags,
    mainHeader,
    sectionSchema,
  };
}
